'use client'

import { useMemo, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Check, ChevronDown, X, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { CategoryDto } from '@/lib/validations/product-submission'

interface CategorySelectorProps {
  categories: CategoryDto[]
  value: number[]
  onChange: (categoryIds: number[]) => void
  isLoading?: boolean
  error?: string
  maxCategories?: number
}

export function CategorySelector({
  categories,
  value,
  onChange,
  isLoading = false,
  error,
  maxCategories = 3,
}: CategorySelectorProps) {
  const [expandedParentId, setExpandedParentId] = useState<number | null>(null)

  // Group categories by parent
  const { parentCategories, childrenByParent, categoryMap } = useMemo(() => {
    const parents: CategoryDto[] = []
    const children: Map<number, CategoryDto[]> = new Map()
    const catMap: Map<number, CategoryDto> = new Map()

    // Debug: log categories structure
    console.log('CategorySelector - categories:', categories)
    console.log('CategorySelector - first category parentId:', categories[0]?.parentId, typeof categories[0]?.parentId)

    categories.forEach((cat) => {
      catMap.set(cat.id, cat)
      // Check for null, undefined, or 0 as "no parent"
      if (cat.parentId === null || cat.parentId === undefined || cat.parentId === 0) {
        parents.push(cat)
      } else {
        const existing = children.get(cat.parentId) || []
        existing.push(cat)
        children.set(cat.parentId, existing)
      }
    })

    console.log('CategorySelector - parents found:', parents.length, parents)

    // Sort parents by sortOrder
    parents.sort((a, b) => a.sortOrder - b.sortOrder)

    // Sort children by sortOrder
    children.forEach((childList) => {
      childList.sort((a, b) => a.sortOrder - b.sortOrder)
    })

    return { parentCategories: parents, childrenByParent: children, categoryMap: catMap }
  }, [categories])

  // Get selected category objects
  const selectedCategories = useMemo(() => {
    return value.map((id) => categoryMap.get(id)).filter(Boolean) as CategoryDto[]
  }, [value, categoryMap])

  const handleParentClick = (parentId: number) => {
    setExpandedParentId(expandedParentId === parentId ? null : parentId)
  }

  const handleCategorySelect = (categoryId: number) => {
    const isSelected = value.includes(categoryId)

    if (isSelected) {
      onChange(value.filter((id) => id !== categoryId))
    } else if (value.length < maxCategories) {
      onChange([...value, categoryId])
    }
  }

  const handleRemoveCategory = (categoryId: number) => {
    onChange(value.filter((id) => id !== categoryId))
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
        <span className="ml-2 text-sm text-foreground-secondary">Загрузка категорий...</span>
      </div>
    )
  }

  if (error) {
    return (
      <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-center">
        <p className="text-sm text-destructive">{error}</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Parent categories grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {parentCategories.map((parent) => {
          const hasChildren = childrenByParent.has(parent.id)
          const isExpanded = expandedParentId === parent.id
          const childCategories = childrenByParent.get(parent.id) || []
          const hasSelectedChildren = childCategories.some((child) => value.includes(child.id))

          return (
            <motion.button
              key={parent.id}
              type="button"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => hasChildren && handleParentClick(parent.id)}
              className={cn(
                'px-3 py-2.5 text-sm font-medium rounded-lg border transition-all duration-200 text-left',
                'flex items-center justify-between gap-2',
                isExpanded
                  ? 'bg-primary-subtle border-primary text-primary-text'
                  : hasSelectedChildren
                    ? 'bg-background border-primary text-foreground'
                    : 'bg-background border-border hover:border-border-hover hover:bg-muted/30'
              )}
            >
              <span className="truncate">{parent.name}</span>
              {hasChildren && (
                <ChevronDown
                  className={cn(
                    'h-4 w-4 shrink-0 transition-transform duration-200',
                    isExpanded && 'rotate-180'
                  )}
                />
              )}
            </motion.button>
          )
        })}
      </div>

      {/* Expanded children section */}
      <AnimatePresence>
        {expandedParentId && childrenByParent.has(expandedParentId) && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="rounded-lg border border-border bg-muted/30 p-4">
              <div className="mb-3 flex items-center gap-2">
                <span className="text-xs font-semibold text-primary-text uppercase tracking-wide">
                  {categoryMap.get(expandedParentId)?.name}
                </span>
                <span className="text-xs text-foreground-tertiary">
                  — выберите подкатегории
                </span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {childrenByParent.get(expandedParentId)?.map((child) => {
                  const isSelected = value.includes(child.id)
                  const isDisabled = !isSelected && value.length >= maxCategories

                  return (
                    <motion.button
                      key={child.id}
                      type="button"
                      whileHover={!isDisabled ? { scale: 1.02 } : {}}
                      whileTap={!isDisabled ? { scale: 0.98 } : {}}
                      onClick={() => !isDisabled && handleCategorySelect(child.id)}
                      disabled={isDisabled}
                      className={cn(
                        'px-3 py-2 text-sm font-medium rounded-md border transition-all duration-200',
                        'flex items-center gap-2',
                        isSelected
                          ? 'bg-primary-subtle text-primary-text border-primary'
                          : 'bg-background border-border hover:border-border-hover',
                        isDisabled && 'opacity-40 cursor-not-allowed'
                      )}
                    >
                      {isSelected && <Check className="h-3.5 w-3.5 shrink-0" />}
                      <span className="truncate">{child.name}</span>
                    </motion.button>
                  )
                })}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Selected categories */}
      {selectedCategories.length > 0 && (
        <div className="pt-2 border-t border-border">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-foreground-secondary uppercase tracking-wide">
              Выбрано
            </span>
            <span className="text-xs text-foreground-tertiary">
              {selectedCategories.length}/{maxCategories}
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            {selectedCategories.map((cat) => (
              <motion.div
                key={cat.id}
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-primary-subtle text-primary-text rounded-full text-sm font-medium"
              >
                <span>{cat.name}</span>
                <button
                  type="button"
                  onClick={() => handleRemoveCategory(cat.id)}
                  className="hover:bg-primary-subtle-hover rounded-full p-0.5 transition-colors"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {/* Hint when no selection */}
      {selectedCategories.length === 0 && (
        <p className="text-xs text-foreground-tertiary text-center py-2">
          Выберите категорию выше, чтобы увидеть подкатегории
        </p>
      )}
    </div>
  )
}
